
import axios from 'axios';

// Environment Detection Strategy (Fortune 500 Standard)
const detectEnvironment = (): 'replit' | 'production' | 'development' => {
  // Check for Replit-specific environment variables
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Replit environments
    if (hostname.includes('replit.dev') || 
        hostname.includes('replit.app') || 
        hostname.includes('repl.co') ||
        hostname.includes('proxy.replit.dev')) {
      return 'replit';
    }
    
    // Production Cloudflare Pages
    if (hostname.includes('pages.dev')) {
      return 'production';
    }
  }
  
  // Development (localhost)
  return 'development';
};

// Dynamic API URL Configuration (Single Source of Truth)
const getApiUrl = (): string => {
  const env = detectEnvironment();
  
  switch (env) {
    case 'replit':
    case 'development':
      // Use relative paths to leverage Vite proxy (ONLY works in dev mode)
      return '';
    case 'production':
      // CRITICAL: Use Railway backend for Cloudflare Pages (static hosting)
      // Fallback ensures production always has absolute URL
      const productionUrl = import.meta.env.VITE_API_URL || 'https://elysium-pos-production.up.railway.app';
      console.log('🌐 Production API URL:', productionUrl);
      return productionUrl;
    default:
      return '';
  }
};

const API_URL = getApiUrl();

// Validation: Ensure production has absolute URL
if (detectEnvironment() === 'production' && !API_URL) {
  console.error('❌ CRITICAL: Production environment detected but no API_URL configured!');
  console.error('Set VITE_API_URL in Cloudflare Pages environment variables');
}

// Log configuration for debugging (only in development)
if (import.meta.env.DEV) {
  console.log('🔧 Environment:', detectEnvironment());
  console.log('🔧 API URL:', API_URL || 'Using relative paths (Vite proxy)');
}

// Create axios instance with proper configuration
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const authStorage = localStorage.getItem('auth-storage');
    console.log('Auth storage from localStorage:', authStorage);
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage);
        const token = parsed.state?.token || parsed.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error parsing auth storage:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with retry logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized and 403 Forbidden with invalid token
    if (error.response?.status === 401 || (error.response?.status === 403 && (error.response?.data?.error === 'Invalid token' || error.response?.data?.error === 'Token expired'))) {
      console.log('Auth failure detected, clearing storage and redirecting');
      
      const bypassRedirectPages = ['/touch-pos', '/pos', '/inventory'];
      const shouldBypass = bypassRedirectPages.some(path => window.location.pathname.includes(path));

      // Only redirect if not already on login page and not a bypass page
      if (!window.location.pathname.includes('/login') && !shouldBypass) {
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
    }
    
    // Network errors - provide helpful debugging
    if (!error.response) {
      console.error('❌ Network Error:', {
        url: originalRequest?.url,
        baseURL: originalRequest?.baseURL,
        method: originalRequest?.method,
        message: error.message,
        code: error.code
      });
      
      // Add user-friendly error message
      error.userMessage = 'Unable to connect to server. Please check your internet connection.';
    }
    
    // Log API errors for debugging (production-safe)
    if (import.meta.env.DEV && error.response) {
      console.error('API Error:', {
        status: error.response.status,
        url: originalRequest?.url,
        data: error.response.data
      });
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
